import * as React from 'react';
import { useEffect, useState } from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {
    Box,
    Typography,
    Card as MuiCard,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PasswordRulesChecker from "../components/passwordRuleChecker/PasswordRuleChecker";
import {postNewPassword, validateResetToken} from "../services/AuthService";
import axios from "axios";
import {TextFieldOutlinedBlueFocus} from "../components/textField/TextFieldVariants";
import {LightHoverButton} from "../components/button/ButtonVariants";

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    maxWidth: '450px',
    alignItems: 'start',
}));

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const [loading, setLoading] = useState(true);
    const [validToken, setValidToken] = useState(false);

    const [passwordTouched, setPasswordTouched] = useState(false);
    const [repeatTouched, setRepeatTouched] = useState(false);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setValidToken(false);
                setLoading(false);
                return;
            }

            try {
                const response = await validateResetToken(token);
                if (response.status === 200) {
                    setValidToken(true);
                } else {
                    setValidToken(false);
                }
            } catch (error) {
                setValidToken(false);
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token]);

    const validatePassword = (pw: string): string | null => {
        const rules = [
            { regex: /.{8,}/, message: 'mind. 8 Zeichen' },
            { regex: /[A-Z]/, message: 'mind. ein Großbuchstabe' },
            { regex: /\d/, message: 'mind. eine Zahl' },
            { regex: /[^A-Za-z0-9]/, message: 'mind. ein Sonderzeichen' }
        ];

        const failed = rules.filter(rule => !rule.regex.test(pw));
        if (failed.length > 0) {
            return 'Passwort muss enthalten: ' + failed.map(r => r.message).join(', ');
        }
        return null;
    };

    const handleSubmit = async () => {
        const pwError = validatePassword(password);
        if (pwError) {
            setError(pwError);
            return;
        }

        if (password !== repeatPassword) {
            setError('Passwörter stimmen nicht überein.');
            return;
        }
        if (token) {
            try {
                const response = await postNewPassword(token, password);
                if (response.status === 200) {
                    setSuccess(true);
                    setError('');
                } else {
                    setError('Ein Fehler ist aufgetreten.');
                }
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    setError(`${error.response?.data.message}`);
                } else {
                    setError(`${error}`);
                }
            }
        }
    };

    const passwordError = passwordTouched && validatePassword(password);
    const repeatError = repeatTouched && password !== repeatPassword;

    return (
        <div className={"centered"}>
            <Card variant="outlined">
                <Box
                    width="100%"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="h1">
                        Neues Passwort setzen
                    </Typography>
                </Box>

                {loading ? (
                    <Box width="100%" textAlign="center">
                        <CircularProgress />
                    </Box>
                ) : !validToken ? (
                    <Typography variant="body1" color="error">
                        Der Link ist ungültig oder abgelaufen.
                    </Typography>
                ) : success ? (
                    <>
                        <Typography variant="body1" color="success.dark">
                            Passwort wurde erfolgreich zurückgesetzt. Du kannst dich jetzt anmelden.
                        </Typography>
                        <Typography variant="body2">
                            <Link
                                to="/login"
                                style={{
                                    fontSize: '16px',
                                    textDecoration: 'none'}}
                            >
                                Zurück zum Login
                            </Link>
                        </Typography>
                    </>
                ) : (
                    <>
                        <TextFieldOutlinedBlueFocus
                            label="Neues Passwort"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onBlur={() => setPasswordTouched(true)}
                            error={!!passwordError}
                            fullWidth
                            required
                        />
                        <PasswordRulesChecker value={password} />

                        <TextFieldOutlinedBlueFocus
                            label="Passwort wiederholen"
                            type="password"
                            value={repeatPassword}
                            onChange={(e) => setRepeatPassword(e.target.value)}
                            onBlur={() => setRepeatTouched(true)}
                            error={repeatError}
                            helperText={repeatError ? 'Passwörter stimmen nicht überein.' : ''}
                            fullWidth
                            required
                        />

                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        <LightHoverButton type="button" fullWidth onClick={handleSubmit}>
                            Passwort zurücksetzen
                        </LightHoverButton>
                    </>
                )}
            </Card>
        </div>
    );
}